import { STREAM_COALESCE_MS, STREAM_MIN_REFETCH_MS, useSessionStore } from '@ctcells/core'
import { useQueryClient } from '@tanstack/react-query'
import { useEffect, useRef } from 'react'

import { createStreamBridge, resyncQueryKeys } from './bridge.ts'
import { pushStreamProgress, resetStreamProgress, seedStreamProgress } from './progress.ts'
import { pushStepLog, resetStepLog } from './stepLog.ts'
import { refreshControlLockState } from '../commands/controlLock.ts'
import { suspendControlLock } from '../controlToken.ts'

/** 구독 계층이 주는 구독 함수. `@ctcells/realtime` 의 `useStreamSubscribe()` 반환값과 같은 모양이다 */
export type SubscribeFn = (
  name: 'events' | 'safety',
  listener: (raw: unknown) => void,
) => () => void

/**
 * 구독 → 캐시 무효화 배선. **앱 셸이 한 번 부른다.**
 *
 * 🔴 `useQueryClient` 를 여기서 부르는 이유가 있다 — `packages/ui` 는 쿼리 라이브러리를
 *    직접 import 하지 못한다(ESLint 가 막는다). 그 규칙의 목적은 「화면이 자기 쿼리를 만들지
 *    못하게」인데, 캐시 접근을 화면 쪽에 한 번 허용하면 그 구멍으로 다 새어 나간다.
 *    그래서 **캐시를 만지는 코드는 전부 이 패키지 안에 둔다.**
 *
 * ⚠ 구독 함수는 인자로 받는다 — 이 패키지는 소켓을 모른다(의존 방향 `api → realtime` 유지).
 *
 * 🔴 **무효화로 안 닿는 축이 하나 있다** — `batch_progress` 는 REST 에 대응하는 값이 없어
 *    (`docs/backend-requests.csv` BR-09) 캐시를 비워 봐야 서버가 다시 줄 것이 없다.
 *    그 한 갈래만 `progress.ts` 보관소가 받는다. **덮어쓰기가 아니라 «없는 축»이다.**
 *    ⚠ 그래서 `bridge.invalidationKeysForEvent` 에도 **없다**(2026-09-21) — 있던 동안은
 *      1Hz 진행 프레임이 `/batches` 를 초당 한 번 다시 긁었다(DP-01 위반 · 실측).
 */
export function useStreamBridge(subscribe: SubscribeFn, coalesceMs = STREAM_COALESCE_MS): void {
  const queryClient = useQueryClient()
  const sessionId = useSessionStore((state) => state.sessionId)
  const authenticated = useSessionStore((state) => state.status === 'authenticated')

  useEffect(() => {
    const bridge = createStreamBridge(queryClient, {
      coalesceMs,
      minIntervalMs: STREAM_MIN_REFETCH_MS,
    })
    const offEvents = subscribe('events', (raw) => {
      bridge.push(raw)
      // 진행 축은 무효화로 못 닿는다 — 같은 프레임을 보관소에도 넣는다
      pushStreamProgress(raw)
      // 스텝 줄도 구독에만 있다 — 진행 보관소 뒤에 넣어야 `execution_id` 귀속이 미러를 본다
      pushStepLog(raw)
    })
    // 안전 스트림도 같은 다리를 탄다 — 페이로드 `type` 이 매핑을 가른다
    const offSafety = subscribe('safety', (raw) => {
      bridge.push(raw)
    })
    return () => {
      offEvents()
      offSafety()
      bridge.dispose()
      // 배선이 끊기면 보관한 진행도 버린다 — 마지막 값이 다음 마운트에서 「지금」인 척하지 않게
      resetStreamProgress()
      resetStepLog()
    }
  }, [queryClient, subscribe, coalesceMs, sessionId, authenticated])
}

/**
 * 구독이 **stale 이면 보관한 진행을 버린다.** 앱 셸이 realtime 의 판정을 넘겨 준다.
 *
 * 🔴 **왜 지우나.** 이 채널에는 `retain` 이 없어 끊긴 동안의 사건이 다시 오지 않는다
 *    (`ctcells-core` `views/stream_relay._connection`). 다시 붙어도 다음 프레임이 올 때까지는
 *    **끊기기 전 값**이 남아 있고, 그것을 그대로 두면 계약이 금한 「마지막 값을 현재로 보이기」가
 *    된다(`events.asyncapi.yaml` `L1EventStream.stale`).
 * ⚠ 값이 다시 차는 것은 **다음 프레임**부터다 — 스냅샷으로 되찾을 창구가 없다(BR-09).
 */
/**
 * **다시 붙으면 한 번 다시 맞춘다**(eric 2026-10-06 「장비랑 시료 프로세스.. 중간중간에 빠졌어」).
 *
 * 🔴 끊긴 동안의 사건은 다시 오지 않는다 — 서버 큐가 넘쳐 끊기거나(`events_stream` 큐 상한) 네트워크가 끊긴 사이에 끝난
 *    장비 실행 · 시료 단계는 다음 같은 사건이 올 때까지 화면에 안 섰다(캐시는 `staleTime: Infinity` · `refetchOnReconnect: false`).
 *    ⇒ «붙었던 채널»이 끊겼다가 다시 붙는 순간에만 `resyncQueryKeys()` 를 한 번 무효화한다. 첫 연결은 아니다(그때는 처음 받는다).
 * 🔴 폴링이 아니다(DP-01) — 「다시 붙었다」는 사실 하나에 한 번이다.
 */
export function useStreamResync(stale: boolean): void {
  const queryClient = useQueryClient()
  const sessionId = useSessionStore((state) => state.sessionId)
  const authenticated = useSessionStore((state) => state.status === 'authenticated')
  const connected = useRef(false)
  const lost = useRef(false)
  useEffect(() => {
    if (stale) {
      suspendControlLock()
      if (connected.current) lost.current = true
      return
    }
    connected.current = true
    if (authenticated) void refreshControlLockState()
    if (!lost.current) return
    lost.current = false
    for (const queryKey of resyncQueryKeys()) void queryClient.invalidateQueries({ queryKey })
  }, [stale, queryClient, sessionId, authenticated])
}

export function useStreamProgressReset(stale: boolean): void {
  useEffect(() => {
    if (stale) resetStreamProgress()
  }, [stale])
}

/**
 * 스냅샷으로 채워도 되는가 — **끊김이 시작된 «뒤»에 받은 목록만** 쓴다(마젠타 지적 · 2026-10-06).
 *
 * 🔴 끊기면 보관소를 비운다(`useStreamProgressReset`). 다시 붙으면(stale → false) 채우기가 다시 도는데, 그때 손에 든
 *    `GET /modules` 캐시가 **끊기기 전에 받은 것**이면 옛 n/m 이 첫 프레임 전까지 「지금」처럼 선다 — 재연결이 목록을 다시
 *    받지 않는다(브리지의 `connection` 은 캐시를 안 건드린다). 그래서 «끊김이 시작된 시각 이후에 받은 목록»만 받는다.
 * ⚠ 한 번도 안 붙었던 첫 연결(새로 고친 직후 `connecting`)은 끊김이 아니다 — `outageAt` 이 없어 처음 받은 목록을 그대로 쓴다.
 *   그 목록은 화면을 연 뒤 받은 것이라 옛 값이 아니다.
 * @param updatedAt 목록을 받은 시각(쿼리 `dataUpdatedAt` · epoch ms). 모르면 채우지 않는다
 * @param outageAt 가장 최근 끊김이 시작된 시각. 끊긴 적이 없으면 `undefined`
 */
export function seedAllowed(updatedAt: number | undefined, outageAt: number | undefined): boolean {
  if (updatedAt === undefined || updatedAt <= 0) return false
  return outageAt === undefined || updatedAt >= outageAt
}

/**
 * **스냅샷으로 진행 보관소의 빈 자리를 채운다**(`seedStreamProgress` 머리말). 작업 줄이 이미 받는 `GET /modules` 항목을 넘긴다.
 * 🔴 끊겼으면(stale) 넣지 않는다 — 그때 보관소는 일부러 비워 둔 것이다(`useStreamProgressReset`).
 * 🔴 다시 붙은 뒤에는 **끊김이 시작된 뒤 받은 목록만** 쓴다(`seedAllowed`) — 끊기기 전 캐시로 옛 n/m 을 「지금」처럼 세우지 않는다.
 *    옛 목록뿐이면 채우지 않고 첫 프레임(1Hz `module_status`)을 기다린다. 그 프레임이 목록 무효화도 부르므로 새 목록이 곧 온다.
 * ⚠ 같은 목록이 다시 와도 구독이 이미 말한 모듈은 건너뛴다 — 스냅샷이 프레임을 이기지 않는다.
 */
export function useStreamProgressSeed(
  modules: readonly unknown[] | undefined,
  stale: boolean,
  updatedAt: number | undefined,
): void {
  /* 한 번이라도 붙었었나 · 가장 최근 끊김이 언제 시작됐나 — 렌더와 무관한 기록이라 ref 다 */
  const connected = useRef(false)
  const outageAt = useRef<number | undefined>(undefined)
  useEffect(() => {
    if (stale) {
      /* 붙었던 채널이 끊긴 순간만 «끊김»이다 — 첫 연결 전 `connecting` 은 아니다 */
      if (connected.current && outageAt.current === undefined) outageAt.current = Date.now()
      return
    }
    connected.current = true
    if (modules === undefined || !seedAllowed(updatedAt, outageAt.current)) return
    /* 다시 붙은 뒤 받은 목록으로 채웠다 — 그 끊김은 끝났다. 다음 끊김은 새로 잰다 */
    outageAt.current = undefined
    seedStreamProgress(modules)
  }, [modules, stale, updatedAt])
}
